import React, { useState, useEffect } from 'react';
import { fetchQueries, deleteQuery, updateQueryStatus } from '../utils/queriesApi';
import AdminNavbar from '../components/AdminNavbar';
import './AdminGallery.css'; // Re-using premium styles

export default function AdminInbox() {
    const [queries, setQueries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [selectedQuery, setSelectedQuery] = useState(null);

    const loadQueries = async () => {
        setLoading(true);
        try {
            const data = await fetchQueries();
            setQueries(data);
        } catch (error) {
            console.error("Failed to load queries:", error);
            setMessage({ type: 'danger', text: `Failed to load queries: ${error.message || 'Check database connection'}` });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadQueries();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this query?')) return;
        try {
            await deleteQuery(id);
            setMessage({ type: 'success', text: 'Query deleted successfully!' });
            loadQueries();
        } catch (error) {
            setMessage({ type: 'danger', text: 'Failed to delete query.' });
        }
    };

    const handleToggleStatus = async (query) => {
        const newStatus = query.status === 'read' ? 'unread' : 'read';
        try {
            await updateQueryStatus(query.id, newStatus);
            loadQueries();
        } catch (error) {
            setMessage({ type: 'danger', text: 'Failed to update status.' });
        }
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    return (
        <div className="admin-gallery-container pt-5 pb-5">
            <div className="container">
                <AdminNavbar />

                {message.text && (
                    <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
                        {message.text}
                        <button type="button" className="close" onClick={() => setMessage({ type: '', text: '' })}>
                            <span>&times;</span>
                        </button>
                    </div>
                )}

                <div className="admin-card">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h4>Inquiry Inbox</h4>
                            <p className="text-muted">Manage queries received from the Contact Us page.</p>
                        </div>
                        <button className="btn btn-outline-primary btn-sm" onClick={loadQueries} disabled={loading}>
                            {loading ? 'Refreshing...' : '🔄 Refresh'}
                        </button>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>Status</th>
                                    <th>Sender Details</th>
                                    <th>Subject & Message</th>
                                    <th>Date Received</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {queries.length > 0 ? (
                                    queries.map((q) => (
                                        <tr key={q.id} style={{ opacity: q.status === 'read' ? 0.7 : 1 }}>
                                            <td>
                                                <span 
                                                    className={`badge ${q.status === 'read' ? 'bg-secondary' : 'bg-primary'}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleToggleStatus(q)}
                                                >
                                                    {q.status === 'read' ? 'Read' : 'New'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="fw-bold">{q.name}</div>
                                                <div className="text-muted small">{q.email}</div>
                                            </td>
                                            <td>
                                                <div className="fw-bold text-dark">{q.subject}</div>
                                                <div 
                                                    className="text-muted text-truncate" 
                                                    style={{ maxWidth: '300px', cursor: 'pointer' }}
                                                    onClick={() => setSelectedQuery(q)}
                                                >
                                                    {q.message}
                                                </div>
                                            </td>
                                            <td className="small text-muted">
                                                {formatDate(q.created_at)}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <button 
                                                        className="btn btn-sm btn-outline-info"
                                                        onClick={() => setSelectedQuery(q)}
                                                    >
                                                        👁️ View
                                                    </button>
                                                    <button 
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDelete(q.id)}
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-5 text-muted">
                                            {loading ? 'Loading queries...' : 'No queries found in the inbox.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* View Modal (Simple overlay) */}
            {selectedQuery && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', zIndex: 1000
                }}>
                    <div className="admin-card" style={{ maxWidth: '600px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }}>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <h4>Query Details</h4>
                            <button className="btn btn-link text-dark p-0" onClick={() => setSelectedQuery(null)} style={{ fontSize: '1.5rem', lineHeight: 1 }}>&times;</button>
                        </div>
                        <hr />
                        <div className="mb-3">
                            <label className="fw-bold text-primary small text-uppercase">From</label>
                            <div>{selectedQuery.name} ({selectedQuery.email})</div>
                        </div>
                        <div className="mb-3">
                            <label className="fw-bold text-primary small text-uppercase">Subject</label>
                            <div className="font-weight-bold">{selectedQuery.subject}</div>
                        </div>
                        <div className="mb-3">
                            <label className="fw-bold text-primary small text-uppercase">Message</label>
                            <div style={{ whiteSpace: 'pre-wrap', background: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
                                {selectedQuery.message}
                            </div>
                        </div>
                        <div className="text-end mt-4">
                            <button className="btn btn-secondary" onClick={() => setSelectedQuery(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
